import { byId, isAr, formatNumber, attachCopyButton, setupLiveCalculation } from './calc-common.js';

export function calculateDaysBetween({
  startDateStr,
  endDateStr,
  includeEndDate = false,
  weekendPattern = 'fri_sat', // 'fri_sat' | 'sat_sun' | 'fri_only'
  customHolidays = [] // array of 'YYYY-MM-DD'
}) {
  if (!startDateStr || !endDateStr) return null;

  const start = new Date(startDateStr + 'T00:00:00');
  const end = new Date(endDateStr + 'T00:00:00');

  const isReversed = end < start;
  const d1 = isReversed ? new Date(end) : new Date(start);
  const d2 = isReversed ? new Date(start) : new Date(end);

  const oneDayMs = 24 * 60 * 60 * 1000;
  let totalCalendarDays = Math.round((d2 - d1) / oneDayMs);
  if (includeEndDate) {
    totalCalendarDays += 1;
  }

  // Iterate to count business days vs weekends vs holidays
  let businessDays = 0;
  let weekendDays = 0;
  let holidayDays = 0;

  const holidaySet = new Set(customHolidays);

  const cur = new Date(d1);
  const last = new Date(d2);
  if (!includeEndDate) {
    last.setDate(last.getDate() - 1);
  }

  while (cur <= last) {
    const dayOfWeek = cur.getDay(); // 0 = Sun, 1 = Mon, ..., 5 = Fri, 6 = Sat
    let isWeekend = false;

    if (weekendPattern === 'fri_sat') {
      isWeekend = dayOfWeek === 5 || dayOfWeek === 6;
    } else if (weekendPattern === 'sat_sun') {
      isWeekend = dayOfWeek === 6 || dayOfWeek === 0;
    } else if (weekendPattern === 'fri_only') {
      isWeekend = dayOfWeek === 5;
    }

    const isoStr = cur.toISOString().split('T')[0];
    const isHoliday = holidaySet.has(isoStr);

    if (isWeekend) {
      weekendDays++;
    } else if (isHoliday) {
      holidayDays++;
    } else {
      businessDays++;
    }

    cur.setDate(cur.getDate() + 1);
  }

  // Breakdown in weeks, months, years
  const weeks = Math.floor(totalCalendarDays / 7);
  const remainingDaysAfterWeeks = totalCalendarDays % 7;
  const approxMonths = (totalCalendarDays / 30.4375).toFixed(1);
  const approxYears = (totalCalendarDays / 365.25).toFixed(2);
  const totalHours = totalCalendarDays * 24;
  const totalMinutes = totalHours * 60;

  return {
    isReversed,
    totalCalendarDays,
    businessDays,
    weekendDays,
    holidayDays,
    weeks,
    remainingDaysAfterWeeks,
    approxMonths,
    approxYears,
    totalHours,
    totalMinutes
  };
}

export function initDaysBetweenDates() {
  const container = byId('days-between-dates-app');
  if (!container) return;

  // Set default dates: today and +30 days
  const today = new Date();
  const nextMonth = new Date();
  nextMonth.setDate(today.getDate() + 30);

  const startInp = byId('dbd-start-date');
  const endInp = byId('dbd-end-date');

  if (startInp && !startInp.value) startInp.value = today.toISOString().split('T')[0];
  if (endInp && !endInp.value) endInp.value = nextMonth.toISOString().split('T')[0];

  const quickBtns = document.querySelectorAll('.dbd-quick-btn');
  quickBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const days = parseInt(btn.dataset.days, 10);
      if (!isNaN(days) && startInp && endInp) {
        const base = new Date(startInp.value || today);
        base.setDate(base.getDate() + days);
        endInp.value = base.toISOString().split('T')[0];
        runDbdCalc();
      }
    });
  });

  setupLiveCalculation([
    'dbd-start-date',
    'dbd-end-date',
    'dbd-include-end',
    'dbd-weekend-pattern',
    'dbd-holidays-input'
  ], runDbdCalc);

  attachCopyButton(byId('dbd-copy-btn'), () => {
    const totalDays = byId('dbd-total-days')?.textContent || '';
    const bizDays = byId('dbd-business-days')?.textContent || '';
    return isAr
      ? `إجمالي الأيام: ${totalDays}\nأيام العمل الفعلية: ${bizDays}`
      : `Total Days: ${totalDays}\nBusiness Days: ${bizDays}`;
  });

  const printBtn = byId('dbd-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }
}

function runDbdCalc() {
  const startDateStr = byId('dbd-start-date')?.value;
  const endDateStr = byId('dbd-end-date')?.value;
  const includeEndDate = byId('dbd-include-end')?.checked || false;
  const weekendPattern = byId('dbd-weekend-pattern')?.value || 'fri_sat';
  const holidaysRaw = byId('dbd-holidays-input')?.value || '';

  const customHolidays = holidaysRaw
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(s));

  const res = calculateDaysBetween({
    startDateStr,
    endDateStr,
    includeEndDate,
    weekendPattern,
    customHolidays
  });

  if (!res) return;

  if (byId('dbd-total-days')) byId('dbd-total-days').textContent = `${formatNumber(res.totalCalendarDays)} ${isAr ? 'يوم' : 'Days'}`;
  if (byId('dbd-business-days')) byId('dbd-business-days').textContent = `${formatNumber(res.businessDays)} ${isAr ? 'يوم عمل' : 'Work Days'}`;
  if (byId('dbd-weekend-days')) byId('dbd-weekend-days').textContent = `${formatNumber(res.weekendDays)} ${isAr ? 'يوم عطلة' : 'Weekend Days'}`;
  if (byId('dbd-weeks-text')) {
    byId('dbd-weeks-text').textContent = isAr
      ? `${res.weeks} أسبوع و ${res.remainingDaysAfterWeeks} يوم`
      : `${res.weeks} weeks and ${res.remainingDaysAfterWeeks} days`;
  }
  if (byId('dbd-approx-months')) byId('dbd-approx-months').textContent = `${res.approxMonths} ${isAr ? 'شهر تقريباً' : 'Months approx'}`;
  if (byId('dbd-approx-years')) byId('dbd-approx-years').textContent = `${res.approxYears} ${isAr ? 'سنة تقريباً' : 'Years approx'}`;
  if (byId('dbd-hours')) byId('dbd-hours').textContent = `${formatNumber(res.totalHours)} ${isAr ? 'ساعة' : 'Hours'}`;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDaysBetweenDates);
  } else {
    initDaysBetweenDates();
  }
}
